import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	type PropsWithChildren,
} from 'react';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Stack } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	BFFConsumerEnum,
	BFFProvider,
	RequestStatus,
	useBFFDataProvider,
} from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	NavErrorBoundary,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';

import { ANALYTICS_CHANNEL } from '../constants';
import type {
	fnGetFilteredProductLinks,
	GlobalShortcutsProps,
	MappedGlobalShortcutsItem,
} from '../types';
import { getUrlMaybe } from '../utils/get-url-maybe';
import { GlobalShortcutsWithIntlProvider } from '../utils/locale';
import { UFO_LOAD_HOLD_NAME, UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME } from '../utils/ufo/constants';
import { useGlobalShortcutsData } from '../utils/use-global-shortcuts-data';

import { DragAndDropOptionsProvider } from './drag-and-drop-options-provider';
import { ImageFromUrl } from './image-from-url';
import { Menu } from './menu';
import { MenuSkeletonItem } from './menu-skeleton-item';
import { ShortcutMenuItem } from './shortcut-menu-item';
import { ViewAllAppsItem } from './view-all-apps-item';

const GlobalShortcutsInner = componentWithFG(
	'navx-4321-useglobalshortcutsdata-hook',
	GlobalShortcutsInnerNew,
	GlobalShortcutsInnerOld,
);

export function GlobalShortcuts(props: GlobalShortcutsProps) {
	return (
		<UFOSegment name={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}>
			<ConditionalErrorBoundary
				showFlags
				sentryConfig={{ product: props?.product }}
				location={ERROR_LOCATION__GLOBAL_SHORTCUTS}
				ufoInteractionName={UFO_SEGMENT_GLOBAL_SHORTCUTS_NAME}
			>
				<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SHORTCUTS}>
					<GlobalShortcutsInner {...props} />
				</BFFProvider>
			</ConditionalErrorBoundary>
		</UFOSegment>
	);
}

function GlobalShortcutsInnerNew(props: GlobalShortcutsProps) {
	const { cloudId, orgId, forceStatic, product, shortcutItems: rawShortcutItems } = props;
	const { handleError } = useNavBoundaryContext({
		location: ERROR_LOCATION__GLOBAL_SHORTCUTS,
		// Log backend errors only to Sentry
		// see: https://hello.atlassian.net/wiki/spaces/navx/pages/6439960649/NavX+-+FE+Observability#%3Acatchemall%3A--Error-handling
		sentryConfig: { product },
		disableAnalytics: true,
	});

	const shortcutItems = useGlobalShortcutsData({
		shortcutsConfig: rawShortcutItems,
		cloudId,
		orgId,
		isShortcutsStatic: forceStatic,
		onError: handleError,
		product,
	});

	return (
		<GlobalShortcutsWithIntlProvider>
			<NavigationAnalyticsContext data={{ product: props?.product }}>
				<ConditionalMenu
					{...props}
					shortcutItems={shortcutItems}
					// We don't need to call the BFF in Menu anymore because we already did (see call to useGlobalShortcutsData above).
					forceStatic={true}
				/>
			</NavigationAnalyticsContext>
		</GlobalShortcutsWithIntlProvider>
	);
}

function GlobalShortcutsInnerOld(props: GlobalShortcutsProps) {
	return (
		<GlobalShortcutsWithIntlProvider>
			<NavigationAnalyticsContext data={{ product: props?.product }}>
				<ConditionalMenu {...props} />
			</NavigationAnalyticsContext>
		</GlobalShortcutsWithIntlProvider>
	);
}

function MenuOLD({
	testId,
	shortcutItems = [],
	forceStatic,
	cloudId,
	orgId,
	userId,
	useSkeleton,
	promoteSite,
	productLinksFilter,
	product,
	onUpdateGetFilteredProductLinks,
	enableDragAndDrop,
	viewAllAppsConfig = {},
	openInSameTab,
}: GlobalShortcutsProps) {
	// Update Ref
	const onUpdateGetFilteredProductLinksRef = useRef<any>(onUpdateGetFilteredProductLinks);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;

	// Filter Ref
	const productLinksFilterRef = useRef<any>(productLinksFilter);
	productLinksFilterRef.current = productLinksFilter;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { buildURLAndFetchData, status, productLinks } = useBFFDataProvider({
		request: {},
		cloudId,
		orgId,
		userId,
		forceStatic,
		product,
	});

	/**
	 * Making a stable reference to `productLinks` to avoid `getFilteredProductLinks`
	 * changing and calling `onUpdateGetFilteredProductLinksRef` repeatedly.
	 */
	const productLinksRef = useRef(productLinks);
	useEffect(() => {
		productLinksRef.current = productLinks;
	}, [productLinks]);

	const getFilteredProductLinks = useCallback<fnGetFilteredProductLinks>(
		(apsKey: string) => {
			let filteredProductLinks = productLinksRef.current[apsKey] ?? [];

			if (productLinksFilterRef.current) {
				filteredProductLinks = filteredProductLinks.filter(productLinksFilterRef.current);
			}

			return {
				promotedLink: filteredProductLinks.find(
					(item, _i, arr) => arr.length === 1 || (promoteSite && item.isCurrentSite),
				),
				links: filteredProductLinks.filter(
					(item, _i, arr) => arr.length === 1 || !(promoteSite && item.isCurrentSite),
				),
				status,
			};
		},
		[promoteSite, status],
	);

	const mappedShortcutItems: Array<MappedGlobalShortcutsItem> = useMemo(() => {
		return shortcutItems.map((item, index) => {
			let key = (item.type === 'link' && item.apsKey) || `shortcut-${index}`;
			if (item.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
				key = item?.apsKey ? item.apsKey : `shortcut-${index}`;
			}

			// Render nested items if the dynamic data is available and we are not forcing static rendering
			if (
				!forceStatic &&
				status?.toLowerCase() === RequestStatus.COMPLETE &&
				item.type === 'link' &&
				item.apsKey &&
				productLinks?.[item.apsKey]
			) {
				const { links: filteredProductLinks, promotedLink } = getFilteredProductLinks(item.apsKey);
				const siteName = filteredProductLinks?.[0]?.displayName;

				// Determine whether shortcut is a single site and is current site and return link instead of flyout
				if (filteredProductLinks.length === 1 && !item.url) {
					return {
						...item,
						key,
						url: filteredProductLinks[0].url,
					};
				}

				if (filteredProductLinks.length > 1) {
					return {
						...item,
						siteName,
						key,
						type: 'expand',
						promoted: promotedLink
							? {
									type: 'link',
									icon: <ImageFromUrl src={promotedLink.avatar} alt="" fallback={item.icon} />,
									label: promotedLink.displayName,
									url: promotedLink.url,
								}
							: undefined,
						items: filteredProductLinks.map((link) => ({
							type: 'link',
							icon: <ImageFromUrl src={link.avatar} alt="" fallback={item.icon} />,
							label: link.displayName,
							displayName: link.displayName,
							url: link.url,
						})),
					};
				}
			}

			return {
				...item,
				key,
			};
		});
	}, [shortcutItems, productLinks, forceStatic, status, getFilteredProductLinks]);

	useEffect(() => {
		if (onUpdateGetFilteredProductLinksRef.current) {
			onUpdateGetFilteredProductLinksRef.current(getFilteredProductLinks);
		}
	}, [getFilteredProductLinks]);

	const apsKeys = useMemo(() => {
		return shortcutItems
			.map((item) => (item.type === 'link' && item.apsKey) || '')
			.filter((item) => !!item);
	}, [shortcutItems]);

	const mountedRef = useRef(false);

	useEffect(() => {
		if (
			forceStatic ||
			status?.toLowerCase() !== RequestStatus.COMPLETE ||
			(productLinks && !Object.keys(productLinks).length) ||
			mountedRef.current === true
		) {
			return;
		}

		mountedRef.current = true;
		const missingShortcuts = shortcutItems
			.filter((item) => item.type === 'link')
			.filter((item) => {
				// todo: Should we also include links with missing APS keys? Currently a link could be missing an APS key and not be included in this list.
				return !getUrlMaybe(item) && item.apsKey && !productLinks?.[item.apsKey];
			});
		const intended = shortcutItems.length;
		const rendered = intended - missingShortcuts.length;

		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'cls',
			actionSubject: 'menu',
			actionSubjectId: 'globalNavigation',
			attributes: {
				name: product,
				intended,
				rendered,
				missingAPSKeys: missingShortcuts.map((item) => item.apsKey).filter(Boolean),
			},
		}).fire(ANALYTICS_CHANNEL);
	}, [createAnalyticsEvent, shortcutItems, productLinks, product, forceStatic, status]);

	useEffect(() => {
		if (!forceStatic) {
			buildURLAndFetchData();
		}
	}, [apsKeys, buildURLAndFetchData, forceStatic]);

	const createLinkItem = useCallback(
		(shortcut: MappedGlobalShortcutsItem) => {
			if (status?.toLowerCase() === RequestStatus.COMPLETE) {
				if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
					return (
						<ShortcutMenuItem
							key={shortcut.key}
							shortcut={shortcut}
							dragAndDropEnabled={enableDragAndDrop}
							status={status}
							isInMoreMenu={false}
						/>
					);
				}

				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						openInSameTab={openInSameTab}
						status={status}
						expandItems={
							shortcut.type === 'expand' && (shortcut.items?.length ?? 0 > 1)
								? shortcut.items
								: undefined
						}
						promotedItem={shortcut.type === 'expand' ? shortcut.promoted : undefined}
						siteName={shortcut.type === 'expand' ? shortcut.siteName : undefined}
						product={product}
					/>
				);
			}

			// Show skeleton while BFF is loading
			if (useSkeleton && (status === RequestStatus.LOADING || status === RequestStatus.INIT)) {
				return <MenuSkeletonItem key={shortcut.key} />;
			}

			// We only want to allow static menu expand item if we are forcing static,
			// otherwise it should be showing the singular item as a disabled link until the
			// data is loaded.
			if (forceStatic && shortcut.type === 'expand' && shortcut.items) {
				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						expandItems={shortcut.items}
						forceStatic={forceStatic}
						product={product}
						openInSameTab={openInSameTab}
					/>
				);
			}

			if (shortcut.type === 'component' && fg('navx-3539-custom-shortcut-component-draggable')) {
				return (
					<ShortcutMenuItem
						key={shortcut.key}
						shortcut={shortcut}
						dragAndDropEnabled={enableDragAndDrop}
						status={status}
					/>
				);
			}

			return (
				<ShortcutMenuItem
					key={shortcut.key}
					shortcut={shortcut}
					status={status}
					forceStatic={forceStatic}
					product={product}
					openInSameTab={openInSameTab}
				/>
			);
		},
		[status, useSkeleton, forceStatic, product, openInSameTab, enableDragAndDrop],
	);

	const shortcutList = (
		<MenuList>
			{mappedShortcutItems?.map(createLinkItem)}
			{viewAllAppsConfig?.path && <ViewAllAppsItem {...viewAllAppsConfig} product={product} />}
		</MenuList>
	);

	return (
		<Stack testId={testId}>
			{enableDragAndDrop ? (
				<DragAndDropOptionsProvider enabled={enableDragAndDrop}>
					{shortcutList}
				</DragAndDropOptionsProvider>
			) : (
				shortcutList
			)}
			{!forceStatic && (
				<UFOLoadHold
					name={UFO_LOAD_HOLD_NAME}
					hold={status === RequestStatus.INIT || status === RequestStatus.LOADING}
				/>
			)}
		</Stack>
	);
}

const NoopWrapper = ({ children }: PropsWithChildren) => <Fragment>{children}</Fragment>;
const ConditionalErrorBoundary = componentWithFG(
	'navx-3308-improved-gas-with-error-boundaries',
	NavErrorBoundary,
	NoopWrapper,
);

const ConditionalMenu = componentWithFG(
	'navx-3308-improved-gas-with-error-boundaries',
	Menu,
	MenuOLD,
);
